import { Check, X, Eye, Pencil, Trash2, FileSearch } from "lucide-react";
import { toast } from "sonner";
import AdminListToolbar from "../../components/admin/AdminListToolbar";
import AdminPagination from "../../components/admin/AdminPagination";

export default function AdminUsersTab({
  users,
  usersSearchDraft,
  onUsersSearchDraftChange,
  onUsersSearch,
  usersTotal,
  page,
  totalPages,
  onPageChange,
  onQueueKycReview,
  onApproveKyc,
  onRejectKyc,
  onActivity,
  onEdit,
  onDelete,
}) {
  return (
    <div className="zc-card overflow-hidden overflow-x-auto mt-4 zc-stack-table" data-testid="admin-users">
      <AdminListToolbar
        draft={usersSearchDraft}
        onDraftChange={onUsersSearchDraftChange}
        onSearch={onUsersSearch}
        placeholder="Search by name or email…"
        testid="admin-users-search"
        total={usersTotal}
        shown={users.length}
      />
      <div className="hidden md:grid md:grid-cols-[1.2fr,1.5fr,0.8fr,0.9fr,0.7fr,110px] zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold">
        <div>Name</div><div>Email</div><div>Role</div><div>KYC</div><div>Joined</div><div className="text-right">Actions</div>
      </div>
      {users.length === 0 && (
        <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">No users match this search.</div>
      )}
      {users.map(u => (
        <div key={u.user_id} className="zc-stack-row md:grid md:grid-cols-[1.2fr,1.5fr,0.8fr,0.9fr,0.7fr,110px] md:items-center px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] last:border-0 text-sm" data-testid={`user-row-${u.user_id}`}>
          <div data-label="Name" className="min-w-0">
            <div className="font-semibold truncate">
              {u.display_name || u.name || "—"}
              {u.is_admin && <span className="ml-2 px-2 zc-badge tracking-widest text-[var(--zc-accent)] border border-[var(--zc-accent)] rounded">admin</span>}
            </div>
            <div className="text-2xs font-mono text-[var(--zc-text-3)] truncate mt-0.5">{u.user_id}</div>
          </div>
          <div data-label="Email" className="text-[var(--zc-text-2)] truncate break-all">{u.email || "—"}</div>
          <div data-label="Role" className="text-[var(--zc-text)] capitalize">{u.role}</div>
          <div data-label="KYC" className="capitalize" style={{ color: u.kyc_status === "approved" ? "var(--zc-success)" : u.kyc_status === "rejected" || u.kyc_status === "declined" ? "var(--zc-danger,#dc2626)" : u.kyc_status === "manual_review" ? "var(--zc-warn,#f59e0b)" : "var(--zc-text-3)" }}>{(u.kyc_status || "").replace("_", " ")}</div>
          <div data-label="Joined" className="text-[var(--zc-text-3)] text-xs">{(u.created_at || "").slice(0,10)}</div>
          <div data-label="Actions" className="zc-stack-actions flex justify-start md:justify-end gap-1 flex-wrap">
            {u.kyc_status === "pending" && (
              <button
                onClick={async () => {
                  try { await onQueueKycReview(u.user_id); }
                  catch (e) { toast.error(e.response?.data?.detail || "Could not queue user for review"); }
                }}
                className="px-2 h-8 zc-btn-ghost text-xs flex items-center gap-1"
                style={{ color: "var(--zc-warn,#f59e0b)" }}
                data-testid={`user-queue-kyc-${u.user_id}`}
                title="Queue for manual KYC review"
              >
                <FileSearch size={12} />
              </button>
            )}
            {u.kyc_status === "manual_review" && (
              <>
                <button
                  onClick={async () => {
                    try { await onApproveKyc(u.user_id); }
                    catch (e) { toast.error(e.response?.data?.detail || "Could not approve user"); }
                  }}
                  className="px-2 h-8 zc-btn-ghost text-xs flex items-center gap-1"
                  style={{ color: "var(--zc-success)" }}
                  data-testid={`user-approve-${u.user_id}`}
                  title="Approve KYC"
                >
                  <Check size={12} />
                </button>
                <button
                  onClick={async () => {
                    try { await onRejectKyc(u.user_id); }
                    catch (e) { toast.error(e.response?.data?.detail || "Could not reject user"); }
                  }}
                  className="px-2 h-8 zc-btn-ghost text-xs flex items-center gap-1"
                  style={{ color: "var(--zc-danger,#dc2626)" }}
                  data-testid={`user-reject-kyc-${u.user_id}`}
                  title="Reject KYC"
                >
                  <X size={12} />
                </button>
              </>
            )}
            <button
              onClick={() => onActivity(u)}
              className="px-2 h-8 zc-btn-ghost text-xs flex items-center gap-1"
              data-testid={`user-activity-${u.user_id}`}
              title="View activity"
            >
              <Eye size={12} />
            </button>
            <button
              onClick={() => onEdit(u)}
              className="px-2 h-8 zc-btn-ghost text-xs flex items-center gap-1"
              data-testid={`user-edit-${u.user_id}`}
              title="Edit user"
            >
              <Pencil size={12} />
            </button>
            <button
              onClick={() => onDelete(u)}
              className="px-2 h-8 zc-btn-ghost text-xs flex items-center gap-1"
              style={{ color: "var(--zc-danger,#dc2626)" }}
              data-testid={`user-delete-${u.user_id}`}
              title="Delete user"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ))}
      <AdminPagination page={page} totalPages={totalPages} onPageChange={onPageChange} testid="admin-users-pagination" />
    </div>
  );
}
